/**
 * Billing periods on the client — formatting and form defaults only.
 *
 * Which period an order bills, and whether it can bill one now, is decided on
 * the server (services/invoices/billing-period.js) and arrives as
 * `order.invoiceBilling`. Nothing here re-implements those rules: keeping a
 * second copy in the browser is how the two drift apart.
 */

import i18n from "i18n";
import { vilniusTodayYmd, vilniusYmd } from "utils/vilnius-date";

export type BillingPeriodMode = "current_month" | "previous_month";
export type ScheduleMode = "last_day_of_month" | "specific_day";

export const BILLING_PERIOD_MODES: BillingPeriodMode[] = [
  "current_month",
  "previous_month",
];

/**
 * The mode a schedule implies: the last day of the month bills the month just
 * ending, a specific day bills the month before. The forms switch to this when
 * the schedule changes, the same default the server applies.
 */
export function defaultBillingPeriodMode(
  scheduleMode: ScheduleMode | string | null | undefined
): BillingPeriodMode {
  return scheduleMode === "specific_day" ? "previous_month" : "current_month";
}

/** A day of month the schedule can use: 1–28, since later days are missing in some months. */
export function isValidScheduleDay(value: string | number | null | undefined) {
  const day = Number(value);
  return Number.isInteger(day) && day >= 1 && day <= 28;
}

/** `YYYY-MM-DD` (or a full ISO timestamp) → `YYYY-MM`, for `<input type="month">`. */
export function toMonthInput(value: string | null | undefined): string {
  return value ? String(value).slice(0, 7) : "";
}

/** `YYYY-MM` from a month input → `YYYY-MM-01`, or null when empty. */
export function fromMonthInput(value: string | null | undefined): string | null {
  return value && /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : null;
}

/**
 * `YYYY-MM` the server stores as the billing start when none is chosen, for the
 * field's placeholder: the latest of the period the mode bills today
 * (`capPeriod`, from the server), the order's month and the month it was
 * created — today, for an order not saved yet. Never back in time, so leaving
 * the field empty never bills history.
 */
export function defaultBillingStartMonth({
  capPeriod,
  orderDate,
  createdAt,
}: {
  capPeriod?: string | null;
  orderDate?: string | null;
  createdAt?: string | number | null;
}): string {
  const created =
    createdAt !== null && createdAt !== undefined && createdAt !== ""
      ? vilniusYmd(new Date(createdAt))
      : vilniusTodayYmd();
  return [toMonthInput(capPeriod), toMonthInput(vilniusYmd(orderDate)), toMonthInput(created)]
    .filter(Boolean)
    .sort()
    .pop() || "";
}

/** Months from `from` to `to`, both `YYYY-MM` and inclusive; 0 when `to` is earlier. */
export function monthsInclusive(from: string, to: string): number {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  const n = (ty - fy) * 12 + (tm - fm) + 1;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Last day of the month a period starts in, `YYYY-MM-DD`. */
export function periodEnd(periodFrom: string): string {
  const [year, month] = periodFrom.slice(0, 7).split("-").map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${periodFrom.slice(0, 7)}-${String(last).padStart(2, "0")}`;
}

/**
 * "September 2026" / "2026 m. rugsėjis" for a period start, in the app's
 * current language. Read in UTC: a period is a calendar month, not an instant,
 * and formatting it in the browser's zone could shift 1 September back into
 * August.
 */
export function formatPeriodMonth(periodFrom: string | null | undefined): string {
  if (!periodFrom) return "";
  const [year, month] = String(periodFrom).slice(0, 7).split("-").map(Number);
  if (!year || !month) return "";
  const language = (i18n as any).language as string | undefined;
  return new Intl.DateTimeFormat(language || undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}
